class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, mutationVersion) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.classList.toggle('is-empty', parsedState.item_count === 0);
    if (mutationVersion && window.CartMutations) {
      window.CartMutations.markRendered(
        this.getSectionsToRender().map((section) => section.id),
        mutationVersion
      );
    }
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  get primarySectionId() {
    return 'cart-drawer';
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

function cartDrawerRequest(body) {
  const drawer = document.querySelector('cart-drawer');
  const versionId = window.CartMutations ? window.CartMutations.begin() : 0;
  const renderedSections = drawer ? drawer.getSectionsToRender().map((section) => section.id) : ['cart-drawer', 'cart-icon-bubble'];

  const request = () =>
    fetch(routes.cart_add_url, { ...fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.status) {
          throw new Error(parsedState.description || parsedState.errors || 'Cart error');
        }
        if (drawer) drawer.renderContents(parsedState, versionId);
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer',
          cartData: parsedState,
          renderVersion: versionId,
          renderedSections,
        });
      });

  return window.CartMutations ? window.CartMutations.enqueue(request) : request();
}

document.addEventListener('click', (event) => {
  const crossSellAdd = event.target.closest('.sw-cross-sell__add');
  if (crossSellAdd) {
    event.preventDefault();
    if (crossSellAdd.disabled) return;
    crossSellAdd.disabled = true;
    const body = JSON.stringify({
      id: crossSellAdd.dataset.variantId,
      quantity: 1,
      sections: document.querySelector('cart-drawer')
        ? document.querySelector('cart-drawer').getSectionsToRender().map((section) => section.id)
        : ['cart-drawer', 'cart-icon-bubble'],
      sections_url: window.location.pathname,
    });
    cartDrawerRequest(body).finally(() => {
      crossSellAdd.disabled = false;
    });
    return;
  }

  if (!event.target.closest('.sw-discount-row__btn')) return;
  event.preventDefault();

  const btn = event.target.closest('.sw-discount-row__btn');
  const input = document.getElementById('CartDrawer-DiscountCode');
  const code = (input && input.value ? input.value : '').trim();
  if (!code) {
    if (input) input.focus();
    return;
  }
  if (btn.disabled) return;
  btn.disabled = true;

  const drawer = document.querySelector('cart-drawer');
  const versionId = window.CartMutations ? window.CartMutations.begin() : 0;
  const renderedSections = drawer
    ? drawer.getSectionsToRender().map((section) => section.id)
    : ['cart-drawer', 'cart-icon-bubble'];

  const request = () =>
    fetch(`/discount/${encodeURIComponent(code)}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sections: renderedSections, sections_url: window.location.pathname }),
    })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.status) {
          throw new Error(parsedState.description || 'Invalid discount code');
        }
        window.__sw_discount = code;
        if (drawer) drawer.renderContents(parsedState, versionId);
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'discount',
          cartData: parsedState,
          renderVersion: versionId,
          renderedSections,
        });
      })
      .catch((error) => {
        window.__sw_discount = code;
        const status = document.querySelector('.sw-discount-row__err');
        if (status) status.textContent = error && error.message ? error.message : 'Could not verify code — it will be applied at checkout.';
      })
      .finally(() => {
        btn.classList.add('sw-discount-row__btn--applied');
        btn.textContent = '✓ Added';
        btn.disabled = false;
      });

  if (window.CartMutations) {
    window.CartMutations.enqueue(request);
  } else {
    request();
  }
});
